import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Grid,
  Button,
  CircularProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useLocation, useNavigate } from "react-router-dom";
import { uploadPowerProof } from "../../services/uploadService";
import { fetchStates, fetchBondDetails, addPowerToLargeBond } from "../../services/largeBondService";

import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";

const CreateLargeBond = () => {
  const [formData, setFormData] = useState({
    bondAmount: "",
    requestedBy: "",
    requestDate: new Date().toISOString().split("T")[0],
    authorizationNumber: "",
    callbackPhone: "",
    defendant: {
      firstName: "",
      middleInitial: "",
      lastName: "",
      dob: "",
      age: "",
      ssn: "",
      occupation: "",
      income: "",
      street1: "",
      street2: "",
      city: "",
      state: "",
      zip: "",
      isUsCitizen: true,
      countryOfOrigin: "",
      immigrationStatus: "",
      description: "",
      isTransferBond: false,
      tbondOriginDescription: "",
    },
    bondConditions: {
      courtOrderedMonitor: false,
      agentRequiredMonitor: false,
      pretrialIntervention: false,
      drugTesting: false,
      conditionDescription: "",
      checkInType: "",
      checkInFrequency: "",
    },
    indemnitors: (window.location.pathname.includes("create") && !window.location.search.includes("copy=true"))
    ? Array(2).fill().map(() => ({
        relationship: "",
        firstName: "",
        lastName: "",
        occupation: "",
        durationOfEmployment: "",
        collaterals: [
          {
            collateralType: "",
            collateralStreet1: "",
            collateralStreet2: "",
            collateralCity: "",
            collateralState: "",
            collateralZip: "",
            collateralAmount: "",
            liens: "",
          },
        ],
      }))
    : [
        {
          relationship: "",
          firstName: "",
          lastName: "",
          occupation: "",
          durationOfEmployment: "",
          collaterals: [
            {
              collateralType: "",
              collateralStreet1: "",
              collateralStreet2: "",
              collateralCity: "",
              collateralState: "",
              collateralZip: "",
              collateralAmount: "",
              liens: "",
            },
          ],
        },
      ],
    bondDetails: {
      powerNumber: "",
      executionAmount: "",
      executionDate: "",
      offense: "",
      powerProof: null,
    },
  });

  const navigate = useNavigate();
  const [powerProofFiles, setPowerProofFiles] = useState([]); // store file
  const [powerNumberForUpload, setPowerNumberForUpload] = useState("");
  const [bondStatus, setBondStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showContactPopup, setShowContactPopup] = useState(false);
  const location = useLocation();
  const [states, setStates] = useState([]);
  const [bondId, setBondId] = useState(null);
  const [refNumber, setRefNumber] = useState(null);
  const [originalPowerNumber, setOriginalPowerNumber] = useState("");
  const [originalExecutionAmount, setOriginalExecutionAmount] = useState("");
  const [isLoadingBond, setIsLoadingBond] = useState(false);
  const isEdit = location.pathname.includes("edit-large-bond");
  const isApproved = bondStatus === "AP" || bondStatus === "Approved";
  const formDisabled = isApproved;
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [existingDetails, setExistingDetails] = useState("");
  const isCopy =
    location.pathname.includes("create") &&
    location.search.includes("copy=true");

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const dataParam = query.get("data");
    const idParam = query.get("id");

    if (dataParam) {
      try {
        const parsed = JSON.parse(decodeURIComponent(dataParam));
        setBondId(parsed.largeBondId);
        setRefNumber(parsed.refNumber);
      } catch (err) {
        console.warn("❌ Could not parse bond data from URL", err);
      }
    } else if (idParam) {
      setBondId(idParam);
    }
  }, [location.search]);

  useEffect(() => {
    const loadStates = async () => {
      const token = localStorage.getItem("jwtToken");
      const baseURL = process.env.REACT_APP_BASE_URL;
  
      try {
        const data = await fetchStates(token, baseURL);
        setStates(data);
      } catch (err) {
        console.error("❌ Error loading states:", err);
        toast.error("❌ Failed to load states");
      }
    };
  
    loadStates();
  }, []);


  useEffect(() => {
    if (!bondId) return;
  
    const loadBondDetails = async () => {
      setIsLoadingBond(true);
      const token = localStorage.getItem("jwtToken");
      const baseURL = process.env.REACT_APP_BASE_URL;
  
      try {
        const data = await fetchBondDetails(bondId, token, baseURL);
        setExistingDetails(data.additionalDetails || "");
        const query = new URLSearchParams(location.search);
        const isActuallyCopying =
          query.get("copy") === "true" && location.pathname.includes("create");
  
        setBondStatus(data.status || "");
  
        if (isActuallyCopying) {
          setBondId(null);
          setRefNumber(null);
          setFormData((prev) => ({
            ...prev,
            requestDate: new Date().toISOString().split("T")[0],
            authorizationNumber: "",
          }));
        } else {
          setBondId(data.largeBondId);
          setRefNumber(data.refNumber);
        }
  
      
        setFormData((prev) => ({
          ...prev,
          bondAmount:
            data.amount != null
              ? Number(data.amount).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })
              : "",
          requestedBy: data.requestedBy || "",
          callbackPhone: data.callbackPhone || "",
          requestDate: data.dateCreated?.split("T")[0] || prev.requestDate,
          authorizationNumber: data.authorizationNumber || "",
          defendant: {
            ...prev.defendant,
            firstName: data.firstName || "",
            middleInitial: data.middleInitial || "",
            lastName: data.lastName || "",
            dob: data.dateOfBirth?.split("T")[0] || "",
            age: data.age || "",
            ssn: data.ssn || "",
            occupation: data.occupation || "",
            income:
              data.income != null
                ? Number(data.income).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })
                : "",
            street1: data.street1 || "",
            street2: data.street2 || "",
            city: data.city || "",
            state: data.state || "",
            zip: data.zip || "",
            isUsCitizen: data.isUsCitizen ?? true,
            countryOfOrigin: data.countryOfOrigin || "",
            immigrationStatus: data.immigrationStatus || "",
            description: data.description || "",
            isTransferBond: data.isTransferBond ?? false,
            tbondOriginDescription: data.tbondOriginDescription || "",
          },
          bondConditions: {
            ...prev.bondConditions,
            courtOrderedMonitor: data.courtOrderedMonitor || false,
            agentRequiredMonitor: data.agentRequiredMonitor || false,
            pretrialIntervention: data.pretrialIntervention || false,
            drugTesting: data.drugTesting || false,
            checkInType: data.checkInType || "",
            checkInFrequency: data.checkInFrequency || "",
            conditionDescription: data.conditionDescription || "",
          },
          bondDetails: {
            ...prev.bondDetails,
            powerNumber: data.powerNumber || "",
            executionAmount:
            data.executionAmount != null
              ? Number(data.executionAmount).toLocaleString("en-US", {
                  style: "currency",
                  currency: "USD",
                })
              : "",
            executionDate: data.executionDate?.split("T")[0] || "",
            offense: data.offense || "",
          },
          indemnitors: Array.isArray(data.indemnitors)
            ? data.indemnitors.map((ind) => ({
                relationship: ind.relationshipToDefendant || "",
                firstName: ind.firstName || "",
                lastName: ind.lastName || "",
                occupation: ind.occupation || "",
                durationOfEmployment: ind.durationOfEmployment || "",
                collaterals: Array.isArray(ind.collaterals)
                  ? ind.collaterals.map((c) => ({
                      collateralType: c.collateralType || "",
                      collateralAmount:
                        c.collateralAmount != null
                          ? Number(c.collateralAmount).toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                            })
                          : "",
                      liens: c.liens || "",
                      collateralStreet1: c.collateralStreet1 || "",
                      collateralStreet2: c.collateralStreet2 || "",
                      collateralCity: c.collateralCity || "",
                      collateralState: c.collateralState || "",
                      collateralZip: c.collateralZip || "",
                    }))
                  : [],
              }))
            : [],
        }));
        
        setOriginalPowerNumber(data.powerNumber || "");
        setOriginalExecutionAmount(
          data.executionAmount != null
            ? Number(data.executionAmount).toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })
            : ""
        );
      } catch (err) {
        console.error("❌ Error loading bond:", err);
        toast.error("❌ Failed to load bond for editing");
      } finally {
        setIsLoadingBond(false);
      }
    };
  
    loadBondDetails();
  }, [bondId]);


  const parseCurrency = (val) =>
    parseFloat(String(val).replace(/[^\d.]/g, "")) || 0;

  const handleSubmit = async () => {
    if (isSubmitting) return; // prevent double submission
    
    setIsSubmitting(true);
    const token = localStorage.getItem("jwtToken");
    const baseURL = process.env.REACT_APP_BASE_URL;

    const missingFields = [];

    //  bond Info
    if (!formData.bondAmount) missingFields.push("Bond Amount");
    if (!formData.requestedBy) missingFields.push("Requested By");

    //  defendant
    if (!formData.defendant.firstName)
      missingFields.push("Defendant First Name");
    if (!formData.defendant.lastName) missingFields.push("Defendant Last Name");
    if (!formData.defendant.dob) missingFields.push("Defendant Date of Birth");

    //  defendant citizenship
    if (formData.defendant.isUsCitizen === false) {
      if (!formData.defendant.countryOfOrigin)
        missingFields.push("Country of Origin");
      if (!formData.defendant.immigrationStatus)
        missingFields.push("Immigration Status");
    }

    // transfer bond validation
    if (formData.defendant.isTransferBond === true) {
      if (!formData.defendant.tbondOriginDescription)
        missingFields.push("Transfer Bond Origin Description");
    }
    // if approved then 
    if (powerProofFiles) {
      console.log("Uploading power proof:", {
        powerNumber: powerNumberForUpload,
        file: powerProofFiles,
      });
    
      
    }
    

    // indmenitor and collateral validation
    formData.indemnitors.forEach((ind, i) => {
      if (!ind.relationship)
        missingFields.push(`Indemnitor ${i + 1} Relationship to Defendant`);
    
      ind.collaterals.forEach((c, index) => {
        if (!c.collateralType) {
          missingFields.push(`Indemnitor ${i + 1} - Collateral ${index + 1} Type`);
        }
    
        if (c.collateralType === "CH" && !c.collateralAmount) {
          missingFields.push(`Indemnitor ${i + 1} - Collateral ${index + 1} Amount`);
        }
    
        if (c.collateralType === "DE" && !c.collateralStreet1) {
          missingFields.push(`Indemnitor ${i + 1} - Collateral ${index + 1} Address`);
        }
      });
    });

    if (missingFields.length > 0) {
      toast.error(
        `❌ Please fill out the following required fields:\n${missingFields.join(
          ", "
        )}`,
        { autoClose: 5000 }
      );
      setIsSubmitting(false);
      return;
    }

    // Proceed with creation or update
    const payload = {
      BondAmount: parseCurrency(formData.bondAmount),
      RequestedBy: formData.requestedBy,
      CallbackPhone: formData.callbackPhone,
      FirstName: formData.defendant.firstName,
      MiddleInitial: formData.defendant.middleInitial,
      LastName: formData.defendant.lastName,
      DateOfBirth: formData.defendant.dob,
      Age: formData.defendant.age?.toString(),
      Ssn: formData.defendant.ssn,
      Occupation: formData.defendant.occupation,
      Income: String(parseCurrency(formData.defendant.income)),
      Street1: formData.defendant.street1,
      Street2: formData.defendant.street2,
      City: formData.defendant.city,
      State: formData.defendant.state,
      Zip: formData.defendant.zip,
      IsUsCitizen: formData.defendant.isUsCitizen,
      CountryOfOrigin: formData.defendant.countryOfOrigin,
      ImmigrationStatus: formData.defendant.immigrationStatus,
      CitizenDescription: formData.defendant.description,
      IsTransferBond: formData.defendant.isTransferBond,
      TbondOriginDescription: formData.defendant.tbondOriginDescription,

      CourtOrderedMonitor: formData.bondConditions.courtOrderedMonitor,
      AgentRequiredMonitor: formData.bondConditions.agentRequiredMonitor,
      PretrialIntervention: formData.bondConditions.pretrialIntervention,
      DrugTesting: formData.bondConditions.drugTesting,
      CheckInType: formData.bondConditions.checkInType,
      CheckInFrequency: formData.bondConditions.checkInFrequency,
      ConditionDescription: formData.bondConditions.conditionDescription,

      PowerNumber: formData.bondDetails.powerNumber,
      ExecutionAmount:
      formData.bondDetails.executionAmount !== ""
        ? parseFloat(formData.bondDetails.executionAmount.replace(/[^0-9.-]+/g, ""))
        : null,
      ExecutionDate:
        formData.bondDetails.executionDate &&
        formData.bondDetails.executionDate !== ""
          ? new Date(formData.bondDetails.executionDate).toISOString()
          : null,
      Offense: formData.bondDetails.offense,
      AdditionalDetails: additionalDetails,
      IsOnline: true,

      Indemnitors: formData.indemnitors.map((ind) => ({
        FirstName: ind.firstName,
        LastName: ind.lastName,
        Occupation: ind.occupation,
        DurationOfEmployment: ind.durationOfEmployment,
        RelationshipToDefendant: ind.relationship,
        Collaterals: ind.collaterals.map((c) => ({
          CollateralType: c.collateralType,
          CollateralAmount: String(parseCurrency(c.collateralAmount)),
          Liens: c.liens,
          CollateralStreet1: c.collateralStreet1,
          CollateralStreet2: c.collateralStreet2,
          CollateralCity: c.collateralCity,
          CollateralState: c.collateralState,
          CollateralZip: c.collateralZip
        })),
      }))
    };

    try {
      const endpoint = isEdit
        ? `${baseURL}largebond/${bondId}`
        : `${baseURL}largebond/bonds`;

      const response = await fetch(endpoint, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error: ${errorText}`);
      }

      const result = await response.json();
      toast.success(
        `✅ Bond ${result.refNumber} ${
          isEdit ? "updated" : "created"
        } successfully!`,
        { autoClose: 3000 }
      );
      
      if (
        isEdit &&
        isApproved &&
        formData.bondDetails.powerNumber
      ) {
        try {
          await addPowerToLargeBond({
            largeBondId: result.largeBondId,
            powerNumber: formData.bondDetails.powerNumber,
            executionAmount: parseFloat(formData.bondDetails.executionAmount.replace(/[^0-9.-]+/g, "")),
            offense: formData.bondDetails.offense,
          });
          console.log("✅ Power info saved.");
        } catch (err) {
          console.error("❌ Failed to save power info:", err);
          toast.error("❌ Failed to associate power number.");
        }
      }

      setShowContactPopup(true);
    } catch (err) {
      console.error(err);
      toast.error("❌ Error submitting bond. Please try again.", {
        autoClose: 4000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatPhone = (input) => {
    if (!input) return "";
    const cleaned = input.replace(/[^\d]/g, "");
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length === 11) {
      return `${cleaned[0]} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return cleaned;
  };

  const powerFieldsLocked = isApproved &&
  formData.bondDetails.powerNumber &&
  formData.bondDetails.executionAmount;

  const collateralOptions = [
    { value: "CH", label: "Cash" },
    { value: "DE", label: "Property Deed" },
    { value: "IA", label: "Indemnity Agreement" },
  ].sort((a, b) => a.label.localeCompare(b.label));

  const formatAmount = (val) =>
    val != null && !isNaN(val)
      ? `$${parseFloat(val).toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`
      : "";


  return (
    <>
    <Container maxWidth="lg" sx={{ paddingTop: 4, position: 'relative' }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {isEdit ? "Edit Large Bond" : "Create Large Bond"}
      </Typography>

      <Box
        sx={{
          p: 3,
          backgroundColor: "#f9f9f9",
          border: "1px solid #ccc",
          borderRadius: 2,
          position: 'relative',
          opacity: isLoadingBond ? 0.5 : 1,
          pointerEvents: isLoadingBond ? 'none' : 'auto',
          transition: 'opacity 0.3s ease-in-out',
        }}
      >
        {/* Loading overlay */}
        {isLoadingBond && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              zIndex: 10,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: 3,
              borderRadius: 2,
              boxShadow: 3,
            }}
          >
            <CircularProgress size={60} />
            <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
              Loading bond details...
            </Typography>
          </Box>
        )}
        <Typography variant="body1" sx={{ mb: 2 }}>
          This form will allow you to enter a new large bond request. Fill out
          all required fields and submit when ready.
        </Typography>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Bond Information
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Bond Amount"
              required
              fullWidth
              disabled={formDisabled}
              value={formData.bondAmount}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d.]/g, "");
                setFormData({ ...formData, bondAmount: raw });
              }}
              onBlur={() => {
                const amount = parseFloat(formData.bondAmount);
                if (!isNaN(amount)) {
                  setFormData({
                    ...formData,
                    bondAmount: amount.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    }),
                  });
                }
              }}
              onFocus={() => {
                const unformatted = String(formData.bondAmount).replace(/[^\d.]/g, "");
                setFormData({ ...formData, bondAmount: unformatted });
              }}
              inputProps={{ inputMode: "numeric", pattern: "[0-9.]*" }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                sx: {
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": { display: "none" },
                  "& input": { MozAppearance: "textfield" },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Requested By"
              required
              fullWidth
              InputLabelProps={{ shrink: true,
                sx: {
                  "& .MuiInputLabel-asterisk": { color: "red" },
                }, }}
              value={formData.requestedBy}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({ ...formData, requestedBy: e.target.value })
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Callback Phone"
              fullWidth
              inputProps={{ maxLength: 15, inputMode: "numeric", pattern: "[0-9]*" }}
              InputLabelProps={{ shrink: true }}
              value={formData.callbackPhone}
              disabled={formDisabled}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d]/g, "");
                setFormData({ ...formData, callbackPhone: raw });
              }}
              onBlur={() => {
                const formatted = formatPhone(formData.callbackPhone);
                setFormData({ ...formData, callbackPhone: formatted });
              }}
              onFocus={() => {
                const stripped = formData.callbackPhone.replace(/[^\d]/g, "");
                setFormData({ ...formData, callbackPhone: stripped });
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Request Date"
              type="date"
              fullWidth
              value={formData.requestDate}
              InputLabelProps={{ shrink: true }}
              disabled
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Authorization #"
              InputLabelProps={{ shrink: true }}
              value={formData.authorizationNumber}
              disabled
              fullWidth
            />
          </Grid>
        </Grid>
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Defendant Information
        </Typography>
        <Grid container spacing={2} sx={{ mt: 4 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="First Name"
              required
              fullWidth
              InputLabelProps={{ shrink: true,
                sx: {
                  "& .MuiInputLabel-asterisk": { color: "red" },
                }, }}
              value={formData.defendant.firstName}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defendant: {
                    ...formData.defendant,
                    firstName: e.target.value,
                  },
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Middle Initial"
              inputProps={{ maxLength: 1 }}
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.defendant.middleInitial}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defendant: {
                    ...formData.defendant,
                    middleInitial: e.target.value,
                  },
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Last Name"
              required
              fullWidth
              InputLabelProps={{ shrink: true,
                sx: {
                  "& .MuiInputLabel-asterisk": { color: "red" },
                }, }}
              value={formData.defendant.lastName}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defendant: {
                    ...formData.defendant,
                    lastName: e.target.value,
                  },
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              label="Date of Birth"
              type="date"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.defendant.dob}
              disabled={formDisabled}
              onChange={(e) => {
                const dob = e.target.value;
                const age = dob
                  ? new Date().getFullYear() - new Date(dob).getFullYear()
                  : "";
                setFormData({
                  ...formData,
                  defendant: { ...formData.defendant, dob, age },
                });
              }}
            />
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              label="Age"
              type="number"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.defendant.age}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defendant: { ...formData.defendant, age: e.target.value },
                })
              }
              inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
              InputProps={{
                sx: {
                  // to hide arrows in browser
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                    display: "none",
                    WebkitAppearance: "none",
                    margin: 0,
                  },
                  "& input[type=number]": {
                    MozAppearance: "textfield",
                  },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="SSN"
              fullWidth
              inputProps={{ maxLength: 9 }}
              InputLabelProps={{ shrink: true }}
              value={formData.defendant.ssn}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defendant: { ...formData.defendant, ssn: e.target.value },
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Occupation"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.defendant.occupation}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defendant: {
                    ...formData.defendant,
                    occupation: e.target.value,
                  },
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Income / Salary"
              fullWidth
              value={formData.defendant.income}
              disabled={formDisabled}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d.]/g, "");
                setFormData({
                  ...formData,
                  defendant: { ...formData.defendant, income: raw },
                });
              }}
              onBlur={() => {
                const val = parseFloat(formData.defendant.income);
                if (!isNaN(val)) {
                  setFormData({
                    ...formData,
                    defendant: {
                      ...formData.defendant,
                      income: formatAmount(val),
                    },
                  });
                }
              }}
              onFocus={() => {
                const unformatted = String(formData.defendant.income).replace(/[^\d.]/g, "");
                setFormData({
                  ...formData,
                  defendant: { ...formData.defendant, income: unformatted },
                });
              }}
              inputProps={{ inputMode: "numeric", pattern: "[0-9.]*" }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                sx: {
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                    display: "none"
                  },
                  "& input": { MozAppearance: "textfield" }
                }
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Street 1"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.defendant.street1 || ""}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defendant: { ...formData.defendant, street1: e.target.value },
                })
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Street 2"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.defendant.street2 || ""}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defendant: { ...formData.defendant, street2: e.target.value },
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="City"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.defendant.city || ""}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defendant: { ...formData.defendant, city: e.target.value },
                })
              }
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="State"
              select
              fullWidth
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              value={formData.defendant.state || ""}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  defendant: { ...formData.defendant, state: e.target.value },
                })
              }
            >
              <option value="">Select</option>
              {states.map((s) => (
                <option key={s.stateCd} value={s.stateCd}>
                  {s.stateName}
                </option>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Zip"
              fullWidth
              inputProps={{ maxLength: 5 }}
              InputLabelProps={{ shrink: true }}
              value={formData.defendant.zip || ""}
              disabled={formDisabled}
              onChange={(e) => {
                const val = e.target.value;
                if (/^\d{0,5}$/.test(val)) {
                  setFormData({
                    ...formData,
                    defendant: { ...formData.defendant, zip: val },
                  });
                }
              }}
            />
          </Grid>
        </Grid>
        
        {/* Transfer Bond Section */}
        <Grid container spacing={2} sx={{ mt: 3 }}>
          <Grid item xs={12}>
            <Box sx={{ mb: 2 }}>
              <FormLabel component="legend" sx={{ mb: 1 }}>
                Is this a Transfer Bond?
              </FormLabel>
              <RadioGroup
                row
                value={formData.defendant.isTransferBond === null ? "" : formData.defendant.isTransferBond.toString()}
                onChange={(e) => {
                  const value = e.target.value === "true" ? true : e.target.value === "false" ? false : null;
                  setFormData({
                    ...formData,
                    defendant: { 
                      ...formData.defendant, 
                      isTransferBond: value,
                      // clear description if "no" selected
                      tbondOriginDescription: value === false ? "" : formData.defendant.tbondOriginDescription
                    },
                  });
                }}
              >
                <FormControlLabel 
                  value="true" 
                  control={<Radio />} 
                  label="Yes" 
                  disabled={formDisabled}
                />
                <FormControlLabel 
                  value="false" 
                  control={<Radio />} 
                  label="No" 
                  disabled={formDisabled}
                />
              </RadioGroup>
            </Box>
          </Grid>
          
          {/* Conditional Transfer Bond Description Field */}
          {formData.defendant.isTransferBond === true && (
            <Grid item xs={12}>
              <TextField
                label="Where is this Bond posted?"
                fullWidth
                multiline
                minRows={2}
                InputLabelProps={{ shrink: true }}
                value={formData.defendant.tbondOriginDescription}
                disabled={formDisabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    defendant: { 
                      ...formData.defendant, 
                      tbondOriginDescription: e.target.value 
                    },
                  })
                }
                placeholder="Please describe where this transfer bond is posted"
                sx={{ mb: 2 }}
              />
            </Grid>
          )}
        </Grid>

        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Charges
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box sx={{ maxWidth: 400 }}>
              <TextField
                label="Offense / Charge"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={formData.bondDetails.offense}
                disabled={formDisabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bondDetails: {
                      ...formData.bondDetails,
                      offense: e.target.value,
                    },
                  })
                }
              />
            </Box>
          </Grid>
        </Grid>
        {existingDetails && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Previous Details
            </Typography>
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                padding: 1,
                backgroundColor: "#f9f9f9",
                fontStyle: "italic",
                whiteSpace: "pre-wrap"
              }}
            >
              {existingDetails}
            </Box>
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ mt: 3, mb: 1 }}>
          Additional Details (Optional)
        </Typography>
        <TextField
          multiline
          minRows={3}
          fullWidth
          value={additionalDetails}
          disabled={formDisabled}
          onChange={(e) => setAdditionalDetails(e.target.value)}
          placeholder="Enter any other details the compliance team should know"
          InputLabelProps={{ shrink: true }}
        />
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Bond Conditions
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <label>
              <input
                type="checkbox"
                checked={formData.bondConditions.courtOrderedMonitor}
                disabled={formDisabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bondConditions: {
                      ...formData.bondConditions,
                      courtOrderedMonitor: e.target.checked,
                    },
                  })
                }
              />
              &nbsp; Ankle Monitor: Court Order
            </label>
          </Grid>

          <Grid item xs={12} sm={6}>
            <label>
              <input
                type="checkbox"
                checked={formData.bondConditions.agentRequiredMonitor}
                disabled={formDisabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bondConditions: {
                      ...formData.bondConditions,
                      agentRequiredMonitor: e.target.checked,
                    },
                  })
                }
              />
              &nbsp; Ankle Monitor: Agent Required
            </label>
          </Grid>

          <Grid item xs={12} sm={6}>
            <label>
              <input
                type="checkbox"
                checked={formData.bondConditions.pretrialIntervention}
                disabled={formDisabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bondConditions: {
                      ...formData.bondConditions,
                      pretrialIntervention: e.target.checked,
                    },
                  })
                }
              />
              &nbsp; Pretrial Intervention
            </label>
          </Grid>

          <Grid item xs={12} sm={6}>
            <label>
              <input
                type="checkbox"
                checked={formData.bondConditions.drugTesting}
                disabled={formDisabled}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    bondConditions: {
                      ...formData.bondConditions,
                      drugTesting: e.target.checked,
                    },
                  })
                }
              />
              &nbsp; Court Mandated Drug & Alcohol Testing
            </label>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Description of Bond Conditions"
              fullWidth
              multiline
              rows={3}
              InputLabelProps={{ shrink: true }}
              value={formData.bondConditions.conditionDescription}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  bondConditions: {
                    ...formData.bondConditions,
                    conditionDescription: e.target.value,
                  },
                })
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Agency Check-in Type"
              select
              fullWidth
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              value={formData.bondConditions.checkInType}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  bondConditions: {
                    ...formData.bondConditions,
                    checkInType: e.target.value,
                  },
                })
              }
            >
              <option value="">Select</option>
              <option value="None">None</option>
              <option value="Physical">Physical</option>
              <option value="Phone">Phone</option>
              <option value="App w/GPS">App w/GPS</option>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Check-in Frequency"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={formData.bondConditions.checkInFrequency}
              disabled={formDisabled}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  bondConditions: {
                    ...formData.bondConditions,
                    checkInFrequency: e.target.value,
                  },
                })
              }
            />
          </Grid>
        </Grid>
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Indemnitor(s)
        </Typography>

        {formData.indemnitors.map((ind, indIndex) => (
          <Box
          key={indIndex}
          sx={{
            border: "2px solid #ccc",
            p: 2,
            mb: 3,
            position: "relative",
          }}
        >
          {formData.indemnitors.length > 1 && (
            <IconButton
              aria-label="Remove Indemnitor"
              size="small"
              disabled={formDisabled}
              onClick={() => {
                const updated = [...formData.indemnitors];
                updated.splice(indIndex, 1);
                setFormData({ ...formData, indemnitors: updated });
              }}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Indemnitor #{indIndex + 1}
          </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="First Name"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={ind.firstName}
                  disabled={formDisabled}
                  onChange={(e) => {
                    const updated = [...formData.indemnitors];
                    updated[indIndex].firstName = e.target.value;
                    setFormData({ ...formData, indemnitors: updated });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Last Name"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={ind.lastName}
                  disabled={formDisabled}
                  onChange={(e) => {
                    const updated = [...formData.indemnitors];
                    updated[indIndex].lastName = e.target.value;
                    setFormData({ ...formData, indemnitors: updated });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Relationship to Defendant"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={ind.relationship}
                  disabled={formDisabled}
                  onChange={(e) => {
                    const updated = [...formData.indemnitors];
                    updated[indIndex].relationship = e.target.value;
                    setFormData({ ...formData, indemnitors: updated });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Occupation"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={ind.occupation}
                  disabled={formDisabled}
                  onChange={(e) => {
                    const updated = [...formData.indemnitors];
                    updated[indIndex].occupation = e.target.value;
                    setFormData({ ...formData, indemnitors: updated });
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Duration of Employment"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  placeholder="e.g., 2 years 6 months"
                  value={ind.durationOfEmployment}
                  disabled={formDisabled}
                  onChange={(e) => {
                    const updated = [...formData.indemnitors];
                    updated[indIndex].durationOfEmployment = e.target.value;
                    setFormData({ ...formData, indemnitors: updated });
                  }}
                />
              </Grid>
            </Grid>

            <Typography variant="subtitle2" sx={{ mt: 2 }}>
              Collateral(s)
            </Typography>

            {ind.collaterals.map((c, collIndex) => (
              <Box
              key={collIndex}
              sx={{
                border: '1px solid #aaa',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                padding: 2,
                marginBottom: 2
              }}
            >
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Collateral #{collIndex + 1}
              </Typography>
            
              <Grid
                container
                spacing={2}
                sx={{ pl: 1, borderLeft: "3px solid #ccc" }}
              >
                {ind.collaterals.length > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
                    <IconButton
                      size="small"
                      aria-label="Remove Collateral"
                      disabled={formDisabled}
                      onClick={() => {
                        const updated = [...formData.indemnitors];
                        updated[indIndex].collaterals.splice(collIndex, 1);
                        setFormData({ ...formData, indemnitors: updated });
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                )}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Collateral Type"
                    select
                    fullWidth
                    SelectProps={{ native: true }}
                    InputLabelProps={{ shrink: true }}
                    value={c.collateralType}
                    disabled={formDisabled}
                    onChange={(e) => {
                      const updated = [...formData.indemnitors];
                      updated[indIndex].collaterals[collIndex].collateralType =
                        e.target.value;
                      setFormData({ ...formData, indemnitors: updated });
                    }}
                  >
                    <option value="">Select</option>
                    {collateralOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </TextField>
                </Grid>

                {c.collateralType === "DE" && (
                  <>
                    <Grid item xs={12}>
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={formDisabled}
                        onClick={() => {
                          const updated = [...formData.indemnitors];
                          const def = formData.defendant;

                          updated[indIndex].collaterals[collIndex].collateralStreet1 = def.street1 || "";
                          updated[indIndex].collaterals[collIndex].collateralStreet2 = def.street2 || "";
                          updated[indIndex].collaterals[collIndex].collateralCity = def.city || "";
                          updated[indIndex].collaterals[collIndex].collateralState = def.state || "";
                          updated[indIndex].collaterals[collIndex].collateralZip = def.zip || "";

                          setFormData({ ...formData, indemnitors: updated });
                        }}
                      >
                        Same as Defendant Address?
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Street 1"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={c.collateralStreet1}
                        disabled={formDisabled}
                        onChange={(e) => {
                          const updated = [...formData.indemnitors];
                          updated[indIndex].collaterals[collIndex].collateralStreet1 = e.target.value;
                          setFormData({ ...formData, indemnitors: updated });
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        label="Street 2"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={c.collateralStreet2}
                        disabled={formDisabled}
                        onChange={(e) => {
                          const updated = [...formData.indemnitors];
                          updated[indIndex].collaterals[collIndex].collateralStreet2 = e.target.value;
                          setFormData({ ...formData, indemnitors: updated });
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="City"
                        fullWidth
                        InputLabelProps={{ shrink: true }}
                        value={c.collateralCity}
                        disabled={formDisabled}
                        onChange={(e) => {
                          const updated = [...formData.indemnitors];
                          updated[indIndex].collaterals[collIndex].collateralCity = e.target.value;
                          setFormData({ ...formData, indemnitors: updated });
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                    <TextField
                      label="State"
                      select
                      fullWidth
                      SelectProps={{ native: true }}
                      InputLabelProps={{ shrink: true }}
                      value={c.collateralState}
                      disabled={formDisabled}
                      onChange={(e) => {
                        const updated = [...formData.indemnitors];
                        updated[indIndex].collaterals[collIndex].collateralState = e.target.value;
                        setFormData({ ...formData, indemnitors: updated });
                      }}
                    >
                      <option value="">Select</option>
                      {states.map((s) => (
                        <option key={s.stateCd} value={s.stateCd}>
                          {s.stateName}
                        </option>
                      ))}
                    </TextField>
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <TextField
                        label="Zip Code"
                        fullWidth
                        inputProps={{ maxLength: 5 }}
                        InputLabelProps={{ shrink: true }}
                        value={c.collateralZip}
                        disabled={formDisabled}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^\d{0,5}$/.test(value)) {
                            const updated = [...formData.indemnitors];
                            updated[indIndex].collaterals[collIndex].collateralZip = value;
                            setFormData({ ...formData, indemnitors: updated });
                          }
                        }}
                      />
                    </Grid>
                  </>
                )}

                {c.collateralType === "CH" && (
                  <Grid item xs={12} sm={6}>
                    <TextField
                    label="Amount"
                    required
                    fullWidth
                    value={c.collateralAmount || ""}
                    disabled={formDisabled}
                    onChange={(e) => {
                      const updated = [...formData.indemnitors];
                      const raw = e.target.value.replace(/[^\d.]/g, "");
                      updated[indIndex].collaterals[collIndex].collateralAmount = raw;
                      setFormData({ ...formData, indemnitors: updated });
                    }}
                    onBlur={() => {
                      const updated = [...formData.indemnitors];
                      const val = parseFloat(String(c.collateralAmount).replace(/[^\d.]/g, ""));
                      if (!isNaN(val)) {
                        updated[indIndex].collaterals[collIndex].collateralAmount = val.toLocaleString("en-US", {
                          style: "currency",
                          currency: "USD",
                        });
                        setFormData({ ...formData, indemnitors: updated });
                      }
                    }}
                    onFocus={() => {
                      const updated = [...formData.indemnitors];
                      updated[indIndex].collaterals[collIndex].collateralAmount = String(c.collateralAmount).replace(/[^\d.]/g, "");
                      setFormData({ ...formData, indemnitors: updated });
                    }}
                    inputProps={{ inputMode: "numeric", pattern: "[0-9.]*" }}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      sx: {
                        "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                          display: "none"
                        },
                        "& input": { MozAppearance: "textfield" }
                      }
                    }}
                  />
                  </Grid>
                )}

                {c.collateralType === "DE" && (
                  <Grid item xs={12}>
                    <TextField
                      label="Liens"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      value={c.liens}
                      disabled={formDisabled}
                      onChange={(e) => {
                        const updated = [...formData.indemnitors];
                        updated[indIndex].collaterals[collIndex].liens = e.target.value;
                        setFormData({ ...formData, indemnitors: updated });
                      }}
                    />
                  </Grid>
                  )}
              </Grid>
              </Box>
            ))}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                disabled={formDisabled}
                onClick={() => {
                  const updated = [...formData.indemnitors];
                  updated[indIndex].collaterals.push({
                    collateralType: "",
                    collateralAddress: "",
                    collateralAmount: "",
                    liens: "",
                  });
                  setFormData({ ...formData, indemnitors: updated });
                }}
              >
                + Add Collateral
              </Button>
            </Box>
          </Box>
        ))}
        <Box sx={{ mb: 4 }}>
          <Button
            variant="contained"
            color="secondary"
            disabled={formDisabled}
            onClick={() => {
              setFormData({
                ...formData,
                indemnitors: [
                  ...formData.indemnitors,
                  {
                    relationship: "",
                    firstName: "",
                    lastName: "",
                    occupation: "",
                    collaterals: [
                      {
                        collateralType: "",
                        collateralAmount: "",
                        liens: "",
                      },
                    ],
                  },
                ],
              });
            }}
          >
            + Add New Indemnitor
          </Button>
        </Box>
        <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>
          Bond Information
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Power Number"
              InputLabelProps={{ shrink: true }}
              value={formData.bondDetails.powerNumber}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  bondDetails: { ...formData.bondDetails, powerNumber: e.target.value }
                })
              }
              fullWidth
              disabled={!isEdit || !isApproved || (originalPowerNumber && originalExecutionAmount)}
              helperText={
                isEdit && isApproved && originalPowerNumber && originalExecutionAmount
                  ? "Power number is locked after being saved"
                  : ""
              }
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              label="Execution Amount"
              required
              fullWidth
              value={formData.bondDetails.executionAmount}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d.]/g, "");
                setFormData({
                  ...formData,
                  bondDetails: { ...formData.bondDetails, executionAmount: raw },
                });
              }}
              onBlur={() => {
                const val = parseFloat(formData.bondDetails.executionAmount);
                if (!isNaN(val)) {
                  setFormData({
                    ...formData,
                    bondDetails: {
                      ...formData.bondDetails,
                      executionAmount: val.toLocaleString("en-US", {
                        style: "currency",
                        currency: "USD",
                      }),
                    },
                  });
                }
              }}
              onFocus={() => {
                const unformatted = String(formData.bondDetails.executionAmount).replace(/[^\d.]/g, "");
                setFormData({
                  ...formData,
                  bondDetails: {
                    ...formData.bondDetails,
                    executionAmount: unformatted,
                  },
                });
              }}
              inputProps={{ inputMode: "numeric", pattern: "[0-9.]*" }}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                sx: {
                  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
                    display: "none",
                  },
                  "& input": { MozAppearance: "textfield" },
                },
              }}
              disabled={!isEdit || !isApproved || (originalPowerNumber && originalExecutionAmount)}
              helperText={
                isEdit && isApproved && originalPowerNumber && originalExecutionAmount
                  ? "Execution amount is locked after being saved"
                  : ""
              }
            />
          </Grid>


          {isEdit && isApproved && (
            <Grid item xs={12} sm={6}>
              <Typography sx={{ mb: 1 }}>Document Upload</Typography>
              <Box display="flex" flexDirection="column" alignItems="flex-start" gap={1} mt={1}>
                <label htmlFor="powerProofInput">
                  <Button
                    variant="outlined"
                    color="primary"
                    component="span"
                  >
                    Select Files
                  </Button>
                </label>

                <input
                  id="powerProofInput"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  multiple
                  style={{ display: "none" }}
                  onChange={(e) => setPowerProofFiles(Array.from(e.target.files))}
                />

                {powerProofFiles.map((file, idx) => (
                  <Typography variant="body2" key={idx}>
                    Selected: {file.name}
                  </Typography>
                ))}

                <Button
                  variant="contained"
                  color="primary"
                  disabled={!powerProofFiles.length}
                  onClick={async () => {
                    const token = localStorage.getItem("jwtToken");
                    const baseURL = process.env.REACT_APP_BASE_URL;
                    for (const file of powerProofFiles) {
                      await uploadPowerProof({
                        file,
                        largeBondId: bondId,
                        powerNumber: formData.bondDetails.powerNumber,
                        token,
                        baseURL,
                      });
                    }
                    setPowerProofFiles([]);
                  }}
                  sx={{ minWidth: "160px" }}
                >
                  Upload Documents
                </Button>
            </Box>
            </Grid>
          )}
        </Grid>
        <Box
          sx={{ mt: 4, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              if (window.confirm('Are you sure? Any unsaved changes will be lost.')) {
                navigate("/largebond");
              }
            }}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            style={{ padding: "10px 20px" }}
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1, color: 'inherit' }} />
                Saving...
              </>
            ) : (
              'Save Large Bond'
            )}
          </Button>
        </Box>
      </Box>
    </Container>

      <Dialog
        open={showContactPopup}
        onClose={() => {
          setShowContactPopup(false);
          navigate("/largebond");
        }}
      >
        <DialogTitle>Large Bond Request Submitted</DialogTitle>
        <DialogContent>
          <Typography>
            Bankers Surety will contact you within 30 minutes. If you do not
            receive a call, please contact us at 877-778-5245.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              setShowContactPopup(false);
              navigate("/largebond");
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CreateLargeBond;
